import {
  AlertCircle,
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  RefreshCcw,
  ScanLine,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatByteSize, kindLabel } from './format'
import type { LocalAttachment } from './types'

/**
 * Compact chip representing one chat attachment in the composer. Renders:
 *  - an inline thumbnail (or a kind-coded icon)
 *  - filename + size, with status sub-label ("Uploading…", "Failed", ...)
 *  - per-state action buttons (cancel / retry / remove)
 *  - keyboard-accessible "open preview" affordance when ready
 *
 * Stateless — caller owns the list and the actions; this is purely
 * presentational so it's trivial to test against deterministic fixtures.
 */
export function AttachmentChip({
  attachment,
  onRemove,
  onRetry,
  onPreview,
}: {
  attachment: LocalAttachment
  onRemove: (localId: string) => void
  onRetry: (localId: string) => void
  onPreview: (localId: string) => void
}) {
  const status = attachment.status
  const canPreview = status === 'ready'
  const subLabel = describeStatus(attachment)
  const Icon = pickIcon(attachment)

  return (
    <div
      data-testid="attachment-chip"
      data-attachment-id={attachment.localId}
      data-status={status}
      className={cn(
        'group/chip relative flex max-w-[16rem] items-center gap-2 rounded-lg border bg-card px-2 py-1.5 text-left text-xs shadow-soft transition-colors',
        status === 'error' &&
          'border-destructive/40 bg-destructive/5 text-destructive',
        status === 'uploading' && 'border-info/40 bg-info-soft/30',
        status === 'ready' && 'hover:bg-accent/40',
      )}
    >
      <ChipBody
        attachment={attachment}
        Icon={Icon}
        subLabel={subLabel}
        canPreview={canPreview}
        onPreview={onPreview}
      />
      <ChipActions
        attachment={attachment}
        onRemove={onRemove}
        onRetry={onRetry}
      />
    </div>
  )
}

function ChipBody({
  attachment,
  Icon,
  subLabel,
  canPreview,
  onPreview,
}: {
  attachment: LocalAttachment
  Icon: typeof Paperclip
  subLabel: string
  canPreview: boolean
  onPreview: (localId: string) => void
}) {
  const thumb = (
    <Thumbnail
      url={attachment.localPreviewUrl}
      mimeType={attachment.mimeType}
      Icon={Icon}
    />
  )
  const text = (
    <div className="min-w-0 flex-1 space-y-0.5">
      <div
        className="truncate font-medium text-foreground"
        title={attachment.filename}
        data-testid="attachment-chip-filename"
      >
        {attachment.filename}
      </div>
      <div
        className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"
        data-testid="attachment-chip-substatus"
      >
        <span>{kindLabel(attachment.kind)}</span>
        <span aria-hidden>·</span>
        <span>{formatByteSize(attachment.byteSize)}</span>
        {subLabel && (
          <>
            <span aria-hidden>·</span>
            <span
              className={cn(
                attachment.status === 'error' && 'text-destructive',
                attachment.status === 'uploading' && 'text-info-soft-foreground',
              )}
            >
              {subLabel}
            </span>
          </>
        )}
      </div>
    </div>
  )

  if (canPreview) {
    return (
      <button
        type="button"
        onClick={() => onPreview(attachment.localId)}
        data-testid="attachment-chip-open"
        aria-label={`Open preview for ${attachment.filename}`}
        className="flex min-w-0 flex-1 items-center gap-2 rounded outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
      >
        {thumb}
        {text}
      </button>
    )
  }
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {thumb}
      {text}
    </div>
  )
}

function Thumbnail({
  url,
  mimeType,
  Icon,
}: {
  url: string | null
  mimeType: string
  Icon: typeof Paperclip
}) {
  if (url && mimeType.startsWith('image/')) {
    return (
      <img
        src={url}
        alt=""
        className="size-8 shrink-0 rounded object-cover"
        data-testid="attachment-chip-thumb"
      />
    )
  }
  return (
    <span
      aria-hidden
      className="inline-flex size-8 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground"
    >
      <Icon className="size-4" />
    </span>
  )
}

function ChipActions({
  attachment,
  onRemove,
  onRetry,
}: {
  attachment: LocalAttachment
  onRemove: (localId: string) => void
  onRetry: (localId: string) => void
}) {
  if (attachment.status === 'uploading' || attachment.status === 'pending') {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => onRemove(attachment.localId)}
        data-testid="attachment-chip-cancel"
        aria-label={`Cancel upload of ${attachment.filename}`}
        className="size-7 shrink-0"
      >
        <X className="size-3.5" aria-hidden />
      </Button>
    )
  }
  if (attachment.status === 'error') {
    return (
      <div className="flex shrink-0 items-center gap-0.5">
        {!attachment.permanent && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => onRetry(attachment.localId)}
            data-testid="attachment-chip-retry"
            aria-label={`Retry uploading ${attachment.filename}`}
            className="size-7 text-foreground"
          >
            <RefreshCcw className="size-3.5" aria-hidden />
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => onRemove(attachment.localId)}
          data-testid="attachment-chip-remove"
          aria-label={`Remove failed upload ${attachment.filename}`}
          className="size-7 text-foreground"
        >
          <X className="size-3.5" aria-hidden />
        </Button>
      </div>
    )
  }
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      onClick={() => onRemove(attachment.localId)}
      data-testid="attachment-chip-remove"
      aria-label={`Detach ${attachment.filename} from this message`}
      className="size-7 shrink-0"
    >
      <X className="size-3.5" aria-hidden />
    </Button>
  )
}

function describeStatus(att: LocalAttachment): string {
  switch (att.status) {
    case 'pending':
      return 'Queued'
    case 'uploading':
      return 'Uploading…'
    case 'ready':
      return ''
    case 'error':
      return att.permanent ? 'Failed' : 'Retry to send'
  }
}

function pickIcon(att: LocalAttachment) {
  if (att.status === 'uploading' || att.status === 'pending') return Loader2
  if (att.status === 'error') return AlertCircle
  if (att.kind === 'screenshot') return ScanLine
  if (att.kind === 'image') return ImageIcon
  if (att.kind === 'pdf') return FileText
  if (att.kind === 'text') return FileText
  return Paperclip
}
